// Modules
import { FormEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { FormattedMessage } from "react-intl";
import get from "lodash.get";
// Material UI
import {
    Autocomplete,
    Chip,
    Avatar,
    DialogContent,
    Grid,
    TextField,
    Tooltip,
    Fab,
    Typography,
    Alert,
    AlertTitle
} from "@mui/material";
// Actions
import { openSnackbar } from "@/config/store/snackbarReducer";
import {
    createSessionEvent,
    updateSessionEvent,
    deleteSessionEvent,
    deleteSubstitute,
    getPatientEventsSWR,
    updateSubsituteAvailableSessions
} from "@/actions/patient/calendarActions";
import { getTherapistsWithEvents } from "@/actions/shared/calendarManagementActions";
// Hooks
import { useTherapistSelection } from "@/components/Users/hooks";
import { useCountdown } from "@/hooks/useCountdown";
// Components
import FullScreenDialog from "@/components/UITemplate/extended/FullScreenDialog";
import BaseCalendar from "@/components/UITemplate/views/calendar/BaseCalendar";
import AlertDialog from "@/components/UITemplate/extended/AlertDialog";
import ConfirmationDialog from "@/components/UITemplate/extended/ConfirmationDialog";
// Helpers
import api from "@/helpers/api";
import { asyncTryCatch } from "@/helpers/utils";
import { BOOK_FREE_SLOT, DECREMENT, DELETE_SESSION, EVENT_SESSION, INCREMENT } from "@/helpers/constants";
import { filterAndFormatTherapistEvents } from "./helpers/getEventsFromTherapists";
import { calculateWeekRange } from "./helpers/calculateWeekRange";
import { getColorByIndex } from "@/helpers/colors";
import { getTherapist } from "@/helpers/userData";
import { API_ROUTES } from "@/helpers/routes";
// Styles
import { gridSpacing } from "@/styles/constants";
// Assests
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RestoreIcon from "@mui/icons-material/Restore";

export const BOOKED_SUBSTITUTES = "booked_substitutes";

type AlertModalState = {
    opened: boolean;
    action?: () => void;
    title?: string;
    confirmation?: string;
};

type ConfirmationModalState = {
    opened: boolean;
    title?: React.ReactNode;
    message?: React.ReactNode;
    messageId?: string;
    messageValues?: Record<string, any>;
    details?: React.ReactNode[];
    actions: any;
};

export const SubstitutesEditDialog = ({
    open,
    parent,
    substitute,
    handleCloseDialog,
    closeModal,
    triggerDataFetch
}) => {
    const dispatch = useDispatch();
    const assignedTherapist = getTherapist();

    const {
        _id: parentId,
        name: parentName,
        email: parentEmail,
        child: { name: childName },
        permissions: { can_join_grouped_session: canBookGroupedSession } = {}
    } = parent || {};

    const {
        start: startDate,
        end: endDate,
        available_sessions: availableSessions,
        pendingBookings,
        therapists: availableTherapists = []
    } = substitute || {};

    const { calendars, handleTherapistSelectionHook } = useTherapistSelection();
    const { therapistsWithEvents } = getTherapistsWithEvents();
    const { events: bookedEvents } = getPatientEventsSWR(parent?._id ?? "");

    const [alertModal, setAlertModal] = useState<AlertModalState>({ opened: false });
    const [confirmationModal, setConfirmationModal] = useState<ConfirmationModalState>({ opened: false });

    const [weeks, setWeeks] = useState<{ startWeek: Date; endWeek: Date } | undefined>();

    useEffect(() => {
        if (therapists?.length > 0) {
            therapists?.forEach(({ _id }) => handleTherapistSelectionHook(_id, true));
        }

        const { startWeek, endWeek } = calculateWeekRange(startDate, endDate);
        setWeeks({ startWeek, endWeek });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [substitute]);

    const hasAvailableSessions = Number(availableSessions) > 0;
    const hasTemporarySession = !!pendingBookings && Object.keys(pendingBookings).length > 0;

    const { timeLeft } = useCountdown({
        initiatedAt: pendingBookings?.initiated_at,
        duration: 60
    });

    const toggleAlertModal = (
        opened: boolean,
        options?: {
            action?: () => void;
            title?: string;
            confirmation?: string;
        }
    ) => {
        if (opened) {
            setAlertModal({
                opened: true,
                action: options?.action!,
                title: options?.title || "",
                confirmation: options?.confirmation || ""
            });
        } else {
            setAlertModal(prev => ({ ...prev, opened: false }));
            setTimeout(() => {
                setAlertModal(prev => ({ ...prev, action: undefined }));
            }, 500);
        }
    };

    const toggleConfirmationModal = (
        opened: boolean,
        options?: {
            title?: React.ReactNode;
            message?: React.ReactNode;
            messageId?: string;
            messageValues?: Record<string, any>;
            details?: React.ReactNode[];
            actions?: React.ReactNode;
        }
    ) => {
        if (opened) {
            setConfirmationModal({
                opened: true,
                title: options?.title,
                message: options?.message,
                messageId: options?.messageId,
                messageValues: options?.messageValues,
                details: options?.details || [],
                actions: options?.actions || []
            });
        } else {
            setConfirmationModal(prev => ({ ...prev, opened: false }));
            setTimeout(() => {
                setConfirmationModal(prev => ({
                    ...prev,
                    title: undefined,
                    message: undefined,
                    messageId: undefined,
                    messageValues: undefined,
                    details: [],
                    actions: []
                }));
            }, 500);
        }
    };

    const { events, therapists } = filterAndFormatTherapistEvents(
        availableTherapists,
        bookedEvents,
        getColorByIndex,
        assignedTherapist,
        calendars,
        therapistsWithEvents
    );

    const triggerSnackbar = (alertSeverity: string, message: JSX.Element) =>
        dispatch(
            openSnackbar({
                message,
                alertSeverity
            })
        );

    const handleEventCreationAction = ({ userId, data, successCb }) => {
        createSessionEvent({
            userId,
            data,
            params: {},
            dispatch,
            successCb: async (eventId: any) => {
                handleUpdateSessionsAvailable(DECREMENT);
                await asyncTryCatch(async () => {
                    return await api.post({
                        path: API_ROUTES.patient.substitutes.pendingEvents(parent?._id ?? "", substitute.id),
                        data: {
                            pendingBooking: {
                                initiatedAt: new Date().toISOString(),
                                substituteId: substitute?.id || null,
                                event: {
                                    id: eventId,
                                    therapist: data.user,
                                    start: data.start,
                                    end: data.end
                                }
                            }
                        }
                    });
                });
                triggerSnackbar("success", <FormattedMessage id={"success-calendar-event-booked"} />);
                successCb && successCb();
            },
            errorCb: () => {
                triggerSnackbar("error", <FormattedMessage id={"error-calendar-event-creation"} />);
            }
        });
    };

    const handleEventUpdateAction = ({ eventId, update, userId, successCb }) => {
        updateSessionEvent({
            userId,
            eventId,
            data: update,
            params: {},
            dispatch,
            successCb: () => {
                successCb && successCb();
            },
            errorCb: () => {}
        });
    };

    const handleEventDeleteAction = ({ userId, id, successCb }) => {
        deleteSessionEvent({
            userId,
            eventId: id,
            params: {},
            dispatch,
            successCb: async () => {
                handleUpdateSessionsAvailable(INCREMENT);
                await asyncTryCatch(async () => {
                    return await api.delete({
                        path: API_ROUTES.patient.substitutes.pendingEvents(parent?._id ?? "", substitute.id),
                        data: {
                            pendingBooking: { substituteId: substitute.id, eventId: id }
                        }
                    });
                });
                triggerSnackbar("success", <FormattedMessage id={"success-calendar-event-delete"} />);
                successCb && successCb();
            },
            errorCb: () => {
                triggerSnackbar("error", <FormattedMessage id={"error-calendar-event-delete"} />);
            }
        });
    };

    const handleUpdateSessionsAvailable = (action: any) => {
        updateSubsituteAvailableSessions &&
            updateSubsituteAvailableSessions({
                patientId: parentId,
                substituteId: substitute.id,
                data: {
                    action
                },
                dispatch,
                successCb: () => {
                    triggerDataFetch();
                }
            });
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            therapists: therapists
        },
        onSubmit: () => {
            deleteSubstitute &&
                deleteSubstitute({
                    patientId: parentId,
                    substituteId: substitute.id,
                    data: { assignedTherapist },
                    dispatch,
                    successCb: (availableSubsitutes: any) => {
                        if (availableSubsitutes.length === 0) {
                            closeModal("substitute");
                        }
                        toggleAlertModal(false);

                        formik.resetForm();
                        handleTherapistSelectionHook([], null, true);
                        handleCloseDialog();
                        triggerDataFetch();
                        triggerSnackbar("success", <FormattedMessage id={"success-substitute-delete"} />);
                    },
                    errorCb: () => {
                        triggerSnackbar("error", <FormattedMessage id={"error-substitute-delete"} />);
                    }
                });
        }
    });

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        if (e) {
            e.preventDefault();
        }

        formik.handleSubmit(e);
    };

    const calendarProps = {
        initialView: "dayGridWeek",
        validRange: {
            start: new Date(weeks?.startWeek || Date.now()),
            end: new Date(weeks?.endWeek || Date.now())
        },
        headerToolbar: {
            left: "prev,next",
            center: "title",
            right: "dayGridWeek"
        },
        dayMaxEventRows: 10,
        showCreatedBy: false,
        showRecurrence: false
    };

    return (
        <FullScreenDialog
            open={open}
            action={{
                label: "confirm",
                action: e => {
                    hasAvailableSessions
                        ? toggleAlertModal(true, {
                              action: () => handleSubmit(e),
                              title: "amount-of-available-sessions",
                              confirmation: "confirm-subsitute-warning-confirmation"
                          })
                        : handleSubmit(e);
                }
            }}
            handleOnClose={() => {
                if (hasTemporarySession) {
                    toggleConfirmationModal(true, {
                        title: <FormattedMessage id="close-booking-process-title" />,
                        messageId: "close-booking-process-description",
                        actions: [
                            {
                                label: "book-later-button",
                                action: () => {
                                    formik.resetForm();
                                    handleTherapistSelectionHook([], null, true);
                                    handleCloseDialog();
                                    triggerDataFetch();
                                    toggleConfirmationModal(false);
                                },
                                color: "error"
                            },
                            {
                                label: "book-now-button",
                                action: () => toggleConfirmationModal(false),
                                variant: "contained",
                                autoFocus: true
                            }
                        ]
                    });
                } else {
                    formik.resetForm();
                    handleTherapistSelectionHook([], null, true);
                    handleCloseDialog();
                    triggerDataFetch();
                }
            }}
            title={<FormattedMessage id="available-substitutes-title" />}
        >
            <DialogContent sx={hasTemporarySession ? { padding: "5px 24px" } : undefined}>
                {hasTemporarySession && (
                    <Grid container>
                        <Grid item xs={12} lg={12}>
                            <Alert
                                icon={<RestoreIcon fontSize="inherit" color="primary" />}
                                severity="warning"
                                variant="standard"
                                sx={{
                                    backgroundColor: "#cad8e7",
                                    marginBlock: 2,
                                    mb: 3,
                                    "& .MuiAlert-message": {
                                        width: "100%"
                                    }
                                }}
                            >
                                <AlertTitle color="primary" sx={{ fontSize: "13px" }}>
                                    <FormattedMessage id="complete-your-booking" /> <b>{timeLeft}</b>
                                </AlertTitle>
                                <Typography variant="h6" fontWeight="light" color="primary" lineHeight={1.5}>
                                    <FormattedMessage id="session-expiring" />
                                </Typography>
                            </Alert>
                        </Grid>
                    </Grid>
                )}

                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12} md={therapists.length > 3 ? 7 : 4} sx={{ minWidth: "250px" }}>
                        <Autocomplete
                            multiple
                            disablePortal
                            limitTags={5}
                            disableClearable
                            filterSelectedOptions
                            getOptionLabel={option => option.name || ""}
                            isOptionEqualToValue={(option, value) => option?.name === value?.name}
                            noOptionsText={<FormattedMessage id="no-options" />}
                            options={therapists || []}
                            onChange={(_, value) => {
                                if (value.length > 0) {
                                    const therapistIds = value.map(val => val?._id);
                                    const selectedTherapistsArray = Object.keys(calendars);
                                    const removedTherapists = selectedTherapistsArray.filter(
                                        therapistId => !therapistIds.includes(therapistId)
                                    );

                                    value.forEach(({ _id: id }) => handleTherapistSelectionHook(id, true, false));
                                    removedTherapists.forEach(id => handleTherapistSelectionHook(id, false, false));

                                    formik.setFieldValue("therapists", value);
                                }
                            }}
                            renderTags={(value, getTagProps) => {
                                return value.map((option, index) => (
                                    <Chip
                                        key={index}
                                        size="small"
                                        label={option?.name}
                                        {...getTagProps({ index })}
                                        className="therapist-label"
                                        sx={{
                                            "&.therapist-label": {
                                                color: "#fff",
                                                background: option?.color,
                                                margin: "0 2px"
                                            },
                                            "&.therapist-label:first-of-type svg": {
                                                display: "none"
                                            }
                                        }}
                                        disabled={index === 0}
                                    />
                                ));
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    id="therapists"
                                    name="therapists"
                                    label={
                                        <span>
                                            <FormattedMessage id="select-therapists" />
                                        </span>
                                    }
                                    error={
                                        get(formik.touched, "therapists", undefined) &&
                                        Boolean(get(formik.errors, "therapists", undefined))
                                    }
                                    helperText={
                                        get(formik.touched, "therapists", undefined) &&
                                        get(formik.errors, "therapists", undefined)
                                    }
                                />
                            )}
                            value={get(formik.values, "therapists", [])}
                        />
                    </Grid>

                    <Grid item xs={12} lg={10}>
                        <Grid container justifyContent="center" alignItems="center">
                            <Grid item xs={6}>
                                <Chip
                                    avatar={
                                        <Avatar color="primary" sx={{ color: "#fff" }}>
                                            {availableSessions}
                                        </Avatar>
                                    }
                                    label={
                                        <FormattedMessage
                                            id="available-sessions"
                                            values={{ amount: <b>{availableSessions}</b> }}
                                        />
                                    }
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item sx={{ textAlign: "right" }} xs={6}>
                                <Tooltip
                                    placement="top-start"
                                    title={<FormattedMessage id="info-substitute-delete-permissions" />}
                                >
                                    <Fab
                                        size="small"
                                        sx={{
                                            backgroundColor: "#fff",
                                            boxShadow: "none",
                                            ml: 1,
                                            width: 32,
                                            height: 32,
                                            minHeight: 32
                                        }}
                                    >
                                        <InfoOutlinedIcon fontSize="small" />
                                    </Fab>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} lg={10}>
                        <BaseCalendar
                            events={events}
                            isAdmin={false}
                            calendarProps={calendarProps}
                            eventTypeCreation={[EVENT_SESSION]}
                            createEventAction={handleEventCreationAction}
                            updateEventAction={handleEventUpdateAction}
                            deleteEventAction={handleEventDeleteAction}
                            permissions={
                                [DELETE_SESSION, ...(hasAvailableSessions ? [BOOK_FREE_SLOT] : [])] as string[]
                            }
                            bookEventFormProps={{
                                showRecurrence: false,
                                patient: [
                                    {
                                        name: parentName,
                                        email: parentEmail,
                                        childName
                                    }
                                ],
                                flags: { canBookGroupedSession },
                                assignedTherapistId: assignedTherapist?._id,
                                availableSessionsUpdate: handleUpdateSessionsAvailable
                            }}
                        />
                    </Grid>

                    <AlertDialog
                        open={alertModal?.opened || false}
                        handleClose={() => toggleAlertModal(false)}
                        actions={{
                            primary: {
                                action: alertModal?.action || (() => {}),
                                label: "yes"
                            },
                            secondary: {
                                action: () => toggleAlertModal(false),
                                label: "cancel"
                            }
                        }}
                        title={alertModal?.title}
                        value={substitute?.available_sessions}
                        confirmation={alertModal?.confirmation}
                    />

                    <ConfirmationDialog
                        open={confirmationModal.opened}
                        handleClose={() => toggleConfirmationModal(false)}
                        title={confirmationModal.title}
                        messageId={confirmationModal.messageId}
                        messageValues={{ value: <b>{timeLeft}</b> }}
                        details={confirmationModal.details}
                        actions={confirmationModal.actions}
                    />
                </Grid>
            </DialogContent>
        </FullScreenDialog>
    );
};

export default SubstitutesEditDialog;
